import styled from '@emotion/styled';
import {
  List,
  Select,
  SelectOption,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface GradeSubjectOption {
  gradeLabel: string;
  subjectLabel: string;
  section?: string;
  gradeSubjectId: number;
  schoolClassId: number;
  grade: number;
  subject: number;
}

export interface PrepareLessonPlanChoiceProps {
  onYourGeneratedPlansClick: () => void;
  onOurLessonPlansClick: () => void;
  onExternalPartyPlansClick?: () => void;
  gradeLabel?: string;
  subjectLabel?: string;
  section?: string;
  className?: string;
  gradeOptions?: SelectOption[];
  subjectOptions?: SelectOption[];
  selectedGrade?: string;
  selectedSubject?: string;
  onGradeChange?: (option: SelectOption) => void;
  onSubjectChange?: (option: SelectOption) => void;
  showInteractiveFilters?: boolean;
  canAccessOurLessonPlans?: boolean;
  canAccessYourGeneratedPlans?: boolean;
  isExternalLpsEnabled?: boolean;
}

export const PrepareLessonPlanChoice = ({
  onYourGeneratedPlansClick,
  onOurLessonPlansClick,
  onExternalPartyPlansClick,
  gradeLabel,
  subjectLabel,
  section,
  className,
  gradeOptions = [],
  subjectOptions = [],
  selectedGrade,
  selectedSubject,
  onGradeChange,
  onSubjectChange,
  showInteractiveFilters = false,
  canAccessOurLessonPlans = true,
  canAccessYourGeneratedPlans = true,
  isExternalLpsEnabled = false,
}: PrepareLessonPlanChoiceProps) => {
  const hasSelection = Boolean(selectedGrade && selectedSubject);
  const gradeNum = selectedGrade ? Number.parseInt(selectedGrade) : 0;
  const isSecondaryGrade = gradeNum >= 6;
  const ourLessonPlansDisabled =
    showInteractiveFilters &&
    (!hasSelection || !canAccessOurLessonPlans || isSecondaryGrade);
  const yourGeneratedPlansDisabled =
    showInteractiveFilters &&
    (!hasSelection || !canAccessYourGeneratedPlans || isSecondaryGrade);
  const externalPartyPlansDisabled =
    showInteractiveFilters && (!hasSelection || !isSecondaryGrade);

  const getInfoMessage = () => {
    if (!showInteractiveFilters) return null;

    if (!hasSelection) {
      return 'Please select a grade and subject to continue.';
    }

    if (!canAccessYourGeneratedPlans && !canAccessOurLessonPlans && !isSecondaryGrade) {
      return 'No lesson plans are available for this Grade/Subject. Please choose a supported combination.';
    }

    if (!canAccessYourGeneratedPlans && canAccessOurLessonPlans) {
      return 'Your generated lesson plans are not available for this grade and subject combination.';
    }

    if (canAccessYourGeneratedPlans && !canAccessOurLessonPlans) {
      return 'Our lesson plans are not available for this grade and subject combination.';
    }

    if (!isSecondaryGrade && hasSelection) {
      return 'External party lesson plans are only available for secondary grades (Grade 6 and above).';
    }

    return null;
  };

  const infoMessage = getInfoMessage();

  return (
    <StyledContent className={className}>
      <StyledDiv flexDirection="column">
        <Typography variant="textBodyLarge" color="--md-sys-color-on-surface">
          Create or choose a lesson plan for:
        </Typography>
        <Spacing marginTop="md" />

        {showInteractiveFilters ? (
          <FilterContainer>
            <Select
              key={`grade-${selectedGrade}-${gradeOptions.length}`}
              variant="outlined"
              label="Grade"
              options={gradeOptions}
              defaultValue={selectedGrade}
              onChange={(option) => onGradeChange?.(option)}
              width="180px"
              data-cy="grade-filter-select"
            />
            <Select
              key={`subject-${selectedGrade}-${selectedSubject}-${subjectOptions.length}`}
              variant="outlined"
              label="Subject"
              options={subjectOptions}
              defaultValue={selectedSubject}
              onChange={(option) => onSubjectChange?.(option)}
              width="220px"
              data-cy="subject-filter-select"
            />
          </FilterContainer>
        ) : (
          gradeLabel &&
          subjectLabel && (
            <StyledDiv gap={8}>
              <StyledDiv
                padding="6px 12px"
                border="1px solid var(--md-sys-color-outline-variant)"
                radius="8px"
              >
                <Typography variant="textLabelLarge">{subjectLabel}</Typography>
              </StyledDiv>
              <StyledDiv
                padding="6px 12px"
                border="1px solid var(--md-sys-color-outline-variant)"
                radius="8px"
              >
                <Typography variant="textLabelLarge">
                  {gradeLabel} - {section}
                </Typography>
              </StyledDiv>
            </StyledDiv>
          )
        )}
      </StyledDiv>

      <Spacing marginTop="xl" />
      <Typography variant="textTitleLarge" color="--md-sys-color-on-surface">
        How would you like to plan this lesson?
      </Typography>
      <Spacing marginTop="lg" />

      <StyledDiv
        flexDirection="column"
        width="100%"
        disabled={yourGeneratedPlansDisabled}
      >
        <List
          heading="Your Generated Lesson Plans"
          supportingText="Create a fresh lesson using AI, tailored to your class and learning goals."
          listContainerStyle="outline"
          trailingIcon={{
            direction: 'trailing',
            type: 'icon',
            source: 'arrow_right',
          }}
          onClick={
            yourGeneratedPlansDisabled ? undefined : onYourGeneratedPlansClick
          }
          data-cy="your-generated-lesson-plans-button"
        />
      </StyledDiv>

      <Spacing marginTop="md" />

      <StyledDiv
        flexDirection="column"
        width="100%"
        disabled={ourLessonPlansDisabled}
      >
        <List
          heading="Our Lesson Plans"
          supportingText="Browse ready-made lesson plans created by NIETE experts."
          listContainerStyle="outline"
          trailingIcon={{
            direction: 'trailing',
            type: 'icon',
            source: 'arrow_right',
          }}
          onClick={ourLessonPlansDisabled ? undefined : onOurLessonPlansClick}
          data-cy="our-lesson-plans-button"
        />
      </StyledDiv>

      {isExternalLpsEnabled && (
        <>
          <Spacing marginTop="md" />

          <StyledDiv
            flexDirection="column"
            width="100%"
            disabled={externalPartyPlansDisabled}
          >
            <List
              heading="External Party Lesson Plans"
              supportingText="Browse ready-made lesson plans created by external education partners."
              listContainerStyle="outline"
              trailingIcon={{
                direction: 'trailing',
                type: 'icon',
                source: 'arrow_right',
              }}
              onClick={
                externalPartyPlansDisabled
                  ? undefined
                  : onExternalPartyPlansClick
              }
              data-cy="external-party-lesson-plans-button"
            />
          </StyledDiv>
        </>
      )}

      {infoMessage && (
        <>
          <Spacing marginTop="lg" />
          <Typography
            variant="textBodyMedium"
            color="--md-sys-color-on-surface-variant"
          >
            {infoMessage}
          </Typography>
        </>
      )}
    </StyledContent>
  );
};

const StyledContent = styled.div`
  padding: var(--rk-spacing-xl);
  display: flex;
  flex-direction: column;

  ${mq(BreakpointSizes.xs)} {
    padding: var(--rk-spacing-lg);
  }
`;

const FilterContainer = styled(StyledDiv)`
  gap: var(--rk-spacing-md);
  flex-wrap: wrap;
  align-items: center;

  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    align-items: stretch;
    gap: var(--rk-spacing-lg);

    md-outlined-select {
      width: 100% !important;
    }
  }
`;
